import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import analytics from '@/services/analytics.service'

/**
 * Custom hook to track page views and navigation
 * Use this in your main App component or layout components
 */
export const usePageTracking = () => {
    const location = useLocation()

    useEffect(() => {
        // Get a friendly page name from the pathname
        const getPageName = (pathname: string): string => {
            const segments = pathname.split('/').filter(Boolean)

            if (segments.length === 0) return 'dashboard'

            // Handle specific routes
            switch (segments[0]) {
                case 'roadmap':
                    if (segments[1]) return 'roadmap_step'
                    return 'roadmap_overview'
                case 'chat':
                    return 'ai_chat'
                case 'resource':
                    if (segments[1]) return 'resource_detail'
                    return 'resource_library'
                case 'services':
                    return 'services'
                case 'onboarding':
                    return 'onboarding'
                case 'admin':
                    return 'admin_panel'
                case 'login':
                    return 'login'
                case 'register':
                    return 'register'
                case 'profile':
                    return 'profile'
                default:
                    return segments[0] || 'unknown'
            }
        }

        const pageName = getPageName(location.pathname)

        // Update document.title so GA4's built-in page_view picks up the correct page name
        const friendlyTitle = pageName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        document.title = `${friendlyTitle} | BrightPath`

        // Track the page view
        analytics.trackPageView(pageName, {
            full_path: location.pathname,
            search_params: location.search,
            hash: location.hash,
        })
    }, [location])
}

/**
 * Hook specifically for tracking feature usage on specific pages
 */
export const useFeatureTracking = (featureName: string) => {
    return {
        trackAction: (action: string, value?: number) => {
            analytics.trackFeatureUsage(featureName, action, value)
        },
    }
}

export default usePageTracking
